/**
 * CreateCollectionModal — modal for creating or editing a collection.
 *
 * Has name, description, icon picker, color selector, and filter config builder.
 */

import { useState } from 'react'
import { X, Bookmark, Film, Star, Tag, Folder, Image, Music, FileText, Zap, Heart, Eye } from 'lucide-react'
import type { CollectionFilterConfig } from '../../types/fileManager'
import type { ActiveFilters } from './FilterBar'
import { EMPTY_FILTERS } from './filters'

interface CreateCollectionModalProps {
  onClose: () => void
  onCreate: (data: {
    name: string
    description: string
    icon: string
    color: string
    filter_config: CollectionFilterConfig
  }) => Promise<void>
  /** Pre-fill from current active filters. */
  initialFilters?: ActiveFilters
  initialName?: string
}

const ICON_OPTIONS = [
  { value: 'bookmark', icon: <Bookmark className="w-4 h-4" /> },
  { value: 'star', icon: <Star className="w-4 h-4" /> },
  { value: 'tag', icon: <Tag className="w-4 h-4" /> },
  { value: 'film', icon: <Film className="w-4 h-4" /> },
  { value: 'image', icon: <Image className="w-4 h-4" /> },
  { value: 'music', icon: <Music className="w-4 h-4" /> },
  { value: 'file-text', icon: <FileText className="w-4 h-4" /> },
  { value: 'folder', icon: <Folder className="w-4 h-4" /> },
  { value: 'zap', icon: <Zap className="w-4 h-4" /> },
  { value: 'heart', icon: <Heart className="w-4 h-4" /> },
  { value: 'eye', icon: <Eye className="w-4 h-4" /> },
]

const COLOR_OPTIONS = [
  { value: 'red', bg: 'bg-red-500' },
  { value: 'orange', bg: 'bg-orange-500' },
  { value: 'yellow', bg: 'bg-yellow-500' },
  { value: 'green', bg: 'bg-emerald-500' },
  { value: 'blue', bg: 'bg-blue-500' },
  { value: 'purple', bg: 'bg-purple-500' },
]

export default function CreateCollectionModal({
  onClose,
  onCreate,
  initialFilters,
  initialName = '',
}: CreateCollectionModalProps) {
  const [name, setName] = useState(initialName)
  const [description, setDescription] = useState('')
  const [icon, setIcon] = useState('bookmark')
  const [color, setColor] = useState('blue')
  const [saving, setSaving] = useState(false)

  // Convert ActiveFilters to CollectionFilterConfig format
  const filters = initialFilters || EMPTY_FILTERS
  const filterConfig: CollectionFilterConfig = {
    tags: filters.tags.length > 0 ? filters.tags : undefined,
    editorial_status: filters.editorial_status.length > 0 ? filters.editorial_status : undefined,
    priority: filters.priority.length > 0 ? filters.priority : undefined,
    asset_type: filters.asset_type.length > 0 ? filters.asset_type : undefined,
  }

  const handleSubmit = async () => {
    if (!name.trim()) return
    setSaving(true)
    try {
      await onCreate({
        name: name.trim(),
        description: description.trim(),
        icon,
        color,
        filter_config: filterConfig,
      })
      onClose()
    } catch {
      // error handled by parent
    } finally {
      setSaving(false)
    }
  }

  const filterSummary = [
    ...(filters.editorial_status.map(s => s.replace('_', ' '))),
    ...(filters.priority.map(p => p.toUpperCase())),
    ...(filters.asset_type),
    ...(filters.tags.map(t => `#${t}`)),
  ]

  return (
    <div className="rq-modal-overlay" onClick={onClose}>
      <div className="rq-modal-card" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-semibold text-[var(--rq-text)]">Create Collection</h2>
          <button onClick={onClose} className="text-[var(--rq-text-soft)] hover:text-[var(--rq-text)] transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="space-y-4">
          {/* Name */}
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[var(--rq-text-soft)] mb-1 block">Name</label>
            <input
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Collection name..."
              className="w-full rq-input-dark text-sm"
              autoFocus
              onKeyDown={e => e.key === 'Enter' && handleSubmit()}
            />
          </div>

          {/* Description */}
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[var(--rq-text-soft)] mb-1 block">Description</label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Optional description..."
              rows={2}
              className="w-full rq-input-dark text-xs resize-none"
            />
          </div>

          {/* Icon picker */}
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[var(--rq-text-soft)] mb-1 block">Icon</label>
            <div className="flex gap-1.5 flex-wrap">
              {ICON_OPTIONS.map(o => (
                <button
                  key={o.value}
                  onClick={() => setIcon(o.value)}
                  className={`p-2 rounded-lg transition-colors ${
                    icon === o.value
                      ? 'bg-[var(--rq-blue)]/20 text-[var(--rq-blue)] border border-[var(--rq-blue)]/30'
                      : 'bg-white/5 text-[var(--rq-text-soft)] border border-white/10 hover:border-white/20'
                  }`}
                >
                  {o.icon}
                </button>
              ))}
            </div>
          </div>

          {/* Color selector */}
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[var(--rq-text-soft)] mb-1 block">Color</label>
            <div className="flex gap-2">
              {COLOR_OPTIONS.map(c => (
                <button
                  key={c.value}
                  onClick={() => setColor(c.value)}
                  className={`w-6 h-6 rounded-full ${c.bg} transition-all ${
                    color === c.value ? 'ring-2 ring-white/60 ring-offset-1 ring-offset-[var(--rq-surface)]' : 'opacity-50 hover:opacity-80'
                  }`}
                />
              ))}
            </div>
          </div>

          {/* Filter summary */}
          {filterSummary.length > 0 && (
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[var(--rq-text-soft)] mb-1 block">Saved Filters</label>
              <div className="flex gap-1.5 flex-wrap">
                {filterSummary.map((f, i) => (
                  <span key={i} className="fm-tag-pill px-2 py-0.5 text-[10px]">{f}</span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} className="px-4 py-2 text-xs text-[var(--rq-text-soft)] hover:text-[var(--rq-text)] transition-colors">
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!name.trim() || saving}
            className="rq-btn-primary px-4 py-2 text-xs disabled:opacity-50"
          >
            {saving ? 'Creating...' : 'Create Collection'}
          </button>
        </div>
      </div>
    </div>
  )
}
